// Define the API using createApi
import { baseQuery } from "./defaultBaseQuery";
import { createApi } from "@reduxjs/toolkit/query/react";
import { User, TranscriptionMetric } from "./userSlice";
import { GPTResponse } from "./apiSlice";

export interface UserCreateArgs {
  email: string;
  password: string;
  plan: string;
}

export interface UserSubscriptionCreateArgs {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  paymentMethodId: string;
}

export interface SubscriptionDetails {
  planId: string;
  planNickname: string;
  status: string;
  currentPeriodEnd: Date;
  currentPeriodStart: Date;
  cancelAtPeriodEnd?: boolean;
}

export interface BillingHistoryItem {
  id: string;
  amount: number;
  currency: string;
  created: number; // Timestamp
  status: string;
  description: string;
}

export interface PromptPrimaryRefinement {
  id: string;
  title: string;
  promptContent: string;
  secondaryPrompts: PromptSecondaryRefinement[];
}

export interface PromptSecondaryRefinement {
  id: string;
  title: string;
  promptContent: string;
}

export interface Prompt {
  id: string;
  title: string;
  promptContent: string;
  enabled: boolean;
  userId: string;
  primaryPrompts: PromptPrimaryRefinement[];
}

export interface TranscribedTextPromptCreateArgs {
  id: string;
  text: string;
}

export interface PrimaryPromptCreateArgs {
  id: string;
  primaryPromptId: string;
  text: string;
}

export interface SecondaryPromptCreateArgs {
  id: string;
  secondaryPromptId: string;
  text: string;
}

// Add PaymentMethodUpdateArgs interface
export interface PaymentMethodUpdateArgs {
  cardNumber: string;
  expMonth: number;
  expYear: number;
  cvc: string;
  country: string;
  zipCode: string;
}

// Add new interface for subscription creation with planId
export interface SubscriptionCreateArgs {
  paymentMethodId: string;
  planId: string;
  customerName?: {
    firstName: string;
    lastName: string;
  };
  subscription_data?: {
    trial_period_days?: number;
    trial_end?: number;
    trial_from_plan?: boolean;
    isDirectSignup?: boolean;
  };
  billingAddress?: {
    line1: string;
    line2?: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
  };
}

// Add interface for transcription count response
export interface TranscriptionCountResponse {
  count: number;
  limit: number;
  hasActiveSubscription: boolean;
}

// Add interface for transcription metrics response
export interface TranscriptionMetricsResponse {
  metrics: TranscriptionMetric[];
  count: number;
}

const tagTypes = ["userTagTypes"];

export const userApi = createApi({
  reducerPath: "userApi",
  baseQuery: baseQuery,
  tagTypes,
  endpoints: (builder) => ({
    // Create user endpoint
    createUser: builder.mutation<User, UserCreateArgs>({
      query: (user) => ({
        url: "/users/create",
        method: "POST",
        body: user,
      }),
      invalidatesTags: tagTypes,
    }),
    createUserWithSubscription: builder.mutation<
      User,
      UserSubscriptionCreateArgs
    >({
      query: (user) => ({
        url: "/users/create/with-subscription",
        method: "POST",
        body: user,
      }),
      invalidatesTags: tagTypes,
    }),
    login: builder.mutation<User, { email: string; password: string }>({
      query: (credentials) => ({
        url: "/users/login",
        method: "POST",
        body: credentials,
      }),
      invalidatesTags: tagTypes,
    }),
    // Logout endpoint
    logout: builder.mutation<void, void>({
      query: () => ({
        url: "/users/logout",
        method: "POST",
      }),
      invalidatesTags: tagTypes,
    }),
    getUserAttributes: builder.query<User, void>({
      query: () => ({
        // Check if using Clerk authentication based on global variable
        url: (window as any).__clerk_jwt ? "/users/attrs/clerk" : "/users/attrs",
      }),
      // Add retry options to make the request more resilient
      extraOptions: {
        maxRetries: 3, // Retry up to 3 times
      },
      // Add an onError handler to log errors
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error: any) {
          console.error('[userApi] Error fetching user attributes:', error);
          // If it's a 401 error, it might be due to token not being set yet
          if (error?.error?.status === 401) {
            console.log('[userApi] Received 401 - auth token might not be set yet');
          }
        }
      },
      providesTags: tagTypes,
    }),
    // Get subscription details endpoint
    getSubscriptionDetails: builder.query<SubscriptionDetails, void>({
      query: () => ({
        url: "/users/subscription/details",
        method: "GET",
      }),
      // Add a transformResponse to handle potential backend discrepancies
      transformResponse: (response: any): SubscriptionDetails => {
        // Check if the cancelAtPeriodEnd property exists in the response
        // If not, we need to check if the user's willRenew property is false, which would indicate
        // the subscription has been cancelled but is still active
        if (response.cancelAtPeriodEnd === undefined) {
          // Try to get the user from the store to check willRenew
          try {
            const state = (window as any).__redux_store__?.getState();
            const willRenew = state?.user?.user?.willRenew;
            
            // If willRenew is defined and false, it means the subscription is cancelled
            if (willRenew === false) {
              return {
                ...response,
                cancelAtPeriodEnd: true
              };
            }
          } catch (e) {
            console.error('Error accessing Redux state:', e);
          }
        }
        
        return response;
      },
      providesTags: tagTypes,
    }),

    // Cancel subscription endpoint
    cancelSubscription: builder.mutation<void, void>({
      query: () => ({
        url: "/users/subscription/cancel",
        method: "POST",
      }),
      invalidatesTags: tagTypes,
    }),

    // Reactivate subscription endpoint
    reactivateSubscription: builder.mutation<void, void>({
      query: () => ({
        url: "/users/subscription/reactivate",
        method: "POST",
      }),
      invalidatesTags: tagTypes,
    }),

    // Create subscription endpoint
    createSubscription: builder.mutation<User, { 
      paymentMethodId: string;
      customerName?: {
        firstName: string;
        lastName: string;
      };
      billingAddress?: {
        line1?: string;
        line2?: string;
        city?: string;
        state?: string;
        postal_code?: string;
        country?: string;
      };
    }>({
      query: (body) => ({
        url: "/users/subscription/create",
        method: "POST",
        body,
      }),
      invalidatesTags: tagTypes,
    }),
    
    // Create subscription with specific plan endpoint
    createPlanSubscription: builder.mutation<User, SubscriptionCreateArgs>({
      query: (body) => ({
        url: "/users/subscription/create-plan",
        method: "POST",
        body,
      }),
      invalidatesTags: tagTypes,
    }),
    
    // Get transcription count endpoint
    getTranscriptionCount: builder.query<TranscriptionCountResponse, void>({
      query: () => ({
        url: "/users/transcription/count",
        method: "GET",
      }),
      providesTags: tagTypes,
    }),
    
    getBillingHistory: builder.query<BillingHistoryItem[], void>({
      query: () => ({
        url: "/users/billing/all",
        method: "GET",
      }),
      providesTags: tagTypes,
    }),
    getCustomPrompts: builder.query<Prompt[], void>({
      query: () => "/prompts",
      providesTags: tagTypes,
    }),
    createCustomPrompt: builder.mutation<void, { prompt: string }>({
      query: (newPrompt) => ({
        url: "/prompts",
        method: "POST",
        body: newPrompt,
      }),
      invalidatesTags: tagTypes,
    }),
    createPromptDocument: builder.mutation<
      GPTResponse,
      PrimaryPromptCreateArgs
    >({
      query: (newPrompt) => ({
        url: "/prompts/create",
        method: "POST",
        body: newPrompt,
      }),
      invalidatesTags: tagTypes,
    }),
    createTranscribedTextPrompt: builder.mutation<
      GPTResponse,
      TranscribedTextPromptCreateArgs
    >({
      query: (newPrompt) => ({
        url: "/prompts/create/initial",
        method: "POST",
        body: newPrompt,
      }),
      invalidatesTags: tagTypes,
    }),
    createPromptDocumentRefinement: builder.mutation<
      GPTResponse,
      SecondaryPromptCreateArgs
    >({
      query: (newPrompt) => ({
        url: "/prompts/create/refine",
        method: "POST",
        body: newPrompt,
      }),
      invalidatesTags: tagTypes,
    }),

    // New feedback endpoint
    createFeedback: builder.mutation<
      { success: boolean },
      { title: string; description: string }
    >({
      query: (feedback) => ({
        url: "/users/feedback/create",
        method: "POST",
        body: feedback,
      }),
    }),

    // Update payment method endpoint
    updatePaymentMethod: builder.mutation<void, PaymentMethodUpdateArgs>({
      query: (paymentDetails) => ({
        url: "/users/payment-method/update",
        method: "POST",
        body: paymentDetails,
      }),
      invalidatesTags: tagTypes,
    }),

    // Get transcription metrics endpoint
    getTranscriptionMetrics: builder.query<TranscriptionMetricsResponse, void>({
      query: () => ({
        url: "/users/transcription/metrics",
        method: "GET",
      }),
      providesTags: tagTypes,
    }),

    // Get time savings settings endpoint
    getTimeSavingsSettings: builder.query<{
      hourlyRate: number;
      currency: string;
      totalTimeSeconds: number;
      todayTimeSeconds: number;
    }, void>({
      query: () => ({
        url: "/time-savings",
        method: "GET",
      }),
      providesTags: tagTypes,
    }),

    // Update time savings settings endpoint
    updateTimeSavingsSettings: builder.mutation<
      { hourlyRate: number; currency: string },
      { hourlyRate: number; currency: string }
    >({
      query: (settings) => ({
        url: "/time-savings/settings",
        method: "PUT",
        body: settings,
      }),
      invalidatesTags: tagTypes,
    }),
  }),
});

// Export hooks for the API calls
export const {
  useCreateUserMutation,
  useLoginMutation,
  useLogoutMutation,
  useGetUserAttributesQuery,
  useCreateUserWithSubscriptionMutation,
  useGetSubscriptionDetailsQuery,
  useCancelSubscriptionMutation,
  useReactivateSubscriptionMutation,
  useCreateSubscriptionMutation,
  useCreatePlanSubscriptionMutation,
  useGetTranscriptionCountQuery,
  useGetTranscriptionMetricsQuery,
  useGetBillingHistoryQuery,
  useCreateCustomPromptMutation,
  useGetCustomPromptsQuery,
  useCreatePromptDocumentMutation,
  useCreatePromptDocumentRefinementMutation,
  useCreateTranscribedTextPromptMutation,
  useCreateFeedbackMutation,
  useUpdatePaymentMethodMutation,
  useGetTimeSavingsSettingsQuery,
  useUpdateTimeSavingsSettingsMutation,
} = userApi;
